/**
 * OP-4: Work Order Detail Page
 * View WO details, assign device, track, manage lifecycle
 * Respects feature entitlements for conditional display
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { 
  ArrowLeft, Package, MapPin, Thermometer, Clock, User, Wifi,
  Play, CheckCircle, XCircle, Loader2, RefreshCw
} from 'lucide-react';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export default function WorkOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTrackOticAuth();
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [liveTracking, setLiveTracking] = useState(null);
  const [devices, setDevices] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [features, setFeatures] = useState({ temp: { enabled: true }, gps: { enabled: true } });

  useEffect(() => {
    fetchWorkOrder();
    fetchDevices();
    fetchFeatures();
  }, [id]);

  useEffect(() => {
    if (workOrder && ['started', 'in_transit'].includes(workOrder.status)) {
      fetchLiveTracking();
      const interval = setInterval(fetchLiveTracking, 10000);
      return () => clearInterval(interval);
    }
  }, [workOrder?.status]);

  const fetchWorkOrder = async () => {
    try {
      const response = await opsApi.getWorkOrder(id);
      setWorkOrder(response.data);
    } catch (error) {
      toast.error('Failed to load work order');
      navigate('/ops/work-orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await opsApi.getDeviceHealth({});
      setDevices(response.data.devices.filter(d => d.status === 'ACTIVE'));
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await opsApi.getTenantFeatures();
      setFeatures(response.data);
    } catch (error) {
      console.error('Failed to load features:', error);
    }
  };

  const fetchLiveTracking = async () => {
    if (!workOrder?.assigned_device_id) return;
    try {
      const response = await opsApi.getLiveTracking({ work_order_id: id });
      setLiveTracking(response.data);
    } catch (error) {
      console.error('Tracking error:', error);
    }
  };

  const handleAssignDevice = async () => {
    if (!selectedDevice) {
      toast.error('Please select a device');
      return;
    }
    setActionLoading(true);
    try {
      await opsApi.assignWorkOrder(id, { device_id: selectedDevice });
      toast.success('Device assigned');
      setAssignDialogOpen(false);
      fetchWorkOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign device');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStart = async () => {
    setActionLoading(true);
    try {
      await opsApi.startWorkOrder(id);
      toast.success('Work order started');
      fetchWorkOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await opsApi.completeWorkOrder(id);
      toast.success('Work order completed');
      fetchWorkOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await opsApi.cancelWorkOrder(id);
      toast.success('Work order cancelled');
      fetchWorkOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      assigned: 'bg-slate-500/20 text-slate-400 border-slate-500',
      started: 'bg-blue-500/20 text-blue-400 border-blue-500',
      in_transit: 'bg-amber-500/20 text-amber-400 border-amber-500',
      completed: 'bg-green-500/20 text-green-400 border-green-500',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500'
    };
    return <Badge variant="outline" className={`text-base px-4 py-1 ${styles[status] || styles.assigned}`}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!workOrder) return null;

  const mapCenter = workOrder.pickup_lat && workOrder.pickup_lng
    ? [parseFloat(workOrder.pickup_lat), parseFloat(workOrder.pickup_lng)]
    : [20.5937, 78.9629];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/ops/work-orders')}
          className="text-slate-400 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white font-mono">{workOrder.work_order_number}</h1>
            {getStatusBadge(workOrder.status)}
          </div>
          <p className="text-slate-400 mt-1">
            Created {new Date(workOrder.created_at).toLocaleString()}
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchWorkOrder}
          className="border-slate-700 text-slate-300"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Route Info */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Route Information</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Pickup</p>
                  <p className="text-white">{workOrder.pickup_address || 'Not specified'}</p>
                </div>
              </div>
              <div className="ml-5 border-l-2 border-dashed border-slate-700 h-8"></div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Drop</p>
                  <p className="text-white">{workOrder.drop_address || 'Not specified'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Temperature Requirements - Only show if entitled */}
          {features.temp?.enabled && (
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Thermometer className="w-5 h-5 text-blue-400" />
                Temperature Requirements
              </h2>
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-slate-400 text-sm">Min Temperature</p>
                  <p className="text-2xl font-bold text-white">{workOrder.temp_min ?? '-'}°C</p>
                </div>
                <div className="h-12 border-l border-slate-700"></div>
                <div>
                  <p className="text-slate-400 text-sm">Max Temperature</p>
                  <p className="text-2xl font-bold text-white">{workOrder.temp_max ?? '-'}°C</p>
                </div>
                {liveTracking?.current_position?.temperature && (
                  <>
                    <div className="h-12 border-l border-slate-700"></div>
                    <div>
                      <p className="text-slate-400 text-sm">Current Temperature</p>
                      <p className={`text-2xl font-bold ${
                        liveTracking.current_position.temperature >= workOrder.temp_min &&
                        liveTracking.current_position.temperature <= workOrder.temp_max
                          ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {liveTracking.current_position.temperature}°C
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Map - Only show if GPS entitled */}
          {features.gps?.enabled && (
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              <div className="p-4 border-b border-slate-800">
                <h2 className="text-lg font-semibold text-white">Live Tracking</h2>
              </div>
              <div className="h-80">
                <MapContainer 
                  center={mapCenter} 
                  zoom={12} 
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  {/* Pickup marker */}
                  {workOrder.pickup_lat && workOrder.pickup_lng && (
                    <Marker 
                      position={[parseFloat(workOrder.pickup_lat), parseFloat(workOrder.pickup_lng)]}
                      icon={createIcon('green')}
                    >
                      <Popup>
                        <strong>Pickup</strong><br/>
                        {workOrder.pickup_address}
                      </Popup>
                    </Marker>
                  )}
                  {/* Drop marker */}
                  {workOrder.drop_lat && workOrder.drop_lng && (
                    <Marker 
                      position={[parseFloat(workOrder.drop_lat), parseFloat(workOrder.drop_lng)]}
                      icon={createIcon('red')}
                    >
                      <Popup>
                        <strong>Drop</strong><br/>
                        {workOrder.drop_address}
                      </Popup>
                    </Marker>
                  )}
                  {/* Current position */}
                  {liveTracking?.current_position?.latitude && (
                    <Marker 
                      position={[liveTracking.current_position.latitude, liveTracking.current_position.longitude]}
                      icon={createIcon('blue')}
                    >
                      <Popup>
                        <strong>Current Position</strong><br/>
                        {features.temp?.enabled && <>Temp: {liveTracking.current_position.temperature}°C<br/></>}
                        Battery: {liveTracking.current_position.battery_level}%
                      </Popup>
                    </Marker>
                  )}
                  {/* Route line */}
                  {workOrder.pickup_lat && workOrder.drop_lat && (
                    <Polyline
                      positions={[
                        [parseFloat(workOrder.pickup_lat), parseFloat(workOrder.pickup_lng)],
                        [parseFloat(workOrder.drop_lat), parseFloat(workOrder.drop_lng)]
                      ]}
                      color="#0d9488"
                      dashArray="10, 10"
                      weight={2}
                    />
                  )}
                </MapContainer>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Actions & Assignment */}
        <div className="space-y-6">
          {/* Device Assignment */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Wifi className="w-5 h-5 text-teal-400" />
              Device Assignment
            </h2>
            {workOrder.assigned_device_id ? (
              <div className="space-y-3">
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-white font-medium">{liveTracking?.device?.device_uid || 'Device Assigned'}</p>
                  <p className="text-slate-400 text-sm">
                    Status: {liveTracking?.device?.status || 'Unknown'}
                  </p>
                  {liveTracking?.device?.last_seen_at && (
                    <p className="text-slate-500 text-xs mt-1">
                      Last seen: {new Date(liveTracking.device.last_seen_at).toLocaleString()}
                    </p>
                  )}
                </div>
                {liveTracking?.current_position && (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {features.temp?.enabled && liveTracking.current_position.temperature && (
                      <div className="p-2 bg-slate-800/50 rounded">
                        <p className="text-slate-400">Temperature</p>
                        <p className="text-white font-medium">{liveTracking.current_position.temperature}°C</p>
                      </div>
                    )}
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-slate-400">Battery</p>
                      <p className="text-white font-medium">{liveTracking.current_position.battery_level}%</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-slate-400 mb-4">No device assigned</p>
                <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-teal-600 hover:bg-teal-700" data-testid="assign-device-btn">
                      Assign Device
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-slate-800">
                    <DialogHeader>
                      <DialogTitle className="text-white">Assign Device</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-slate-300">Select Device</Label>
                        <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                          <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                            <SelectValue placeholder="Choose a device" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700">
                            {devices.map((device) => (
                              <SelectItem 
                                key={device.device_id} 
                                value={device.device_id}
                                className="text-white"
                              >
                                {device.device_uid} ({device.battery_level}% battery)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        onClick={handleAssignDevice}
                        className="w-full bg-teal-600 hover:bg-teal-700"
                        disabled={actionLoading}
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Assign Device
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Actions</h2>
            <div className="space-y-3">
              {workOrder.status === 'assigned' && (
                <Button 
                  onClick={handleStart}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={actionLoading || !workOrder.assigned_device_id}
                  data-testid="start-wo-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Work Order
                </Button>
              )}
              
              {['started', 'in_transit'].includes(workOrder.status) && (
                <Button 
                  onClick={handleComplete}
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={actionLoading}
                  data-testid="complete-wo-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Completed
                </Button>
              )}
              
              {workOrder.status !== 'completed' && workOrder.status !== 'cancelled' && (
                <Button 
                  onClick={handleCancel}
                  variant="outline"
                  className="w-full border-red-500 text-red-400 hover:bg-red-500/20"
                  disabled={actionLoading}
                  data-testid="cancel-wo-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel Work Order
                </Button>
              )}

              {!workOrder.assigned_device_id && workOrder.status === 'assigned' && (
                <p className="text-amber-400 text-sm text-center">
                  Assign a device to start tracking
                </p>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" />
              Timeline
            </h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                <div>
                  <p className="text-white text-sm">Created</p>
                  <p className="text-slate-500 text-xs">{new Date(workOrder.created_at).toLocaleString()}</p>
                </div>
              </div>
              {workOrder.started_at && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                  <div>
                    <p className="text-white text-sm">Started</p>
                    <p className="text-slate-500 text-xs">{new Date(workOrder.started_at).toLocaleString()}</p>
                  </div>
                </div>
              )}
              {workOrder.completed_at && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  <div>
                    <p className="text-white text-sm">Completed</p>
                    <p className="text-slate-500 text-xs">{new Date(workOrder.completed_at).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
